// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md

import type {
  AuthConfig,
  CatalogCredentialRole,
  AuthInject,
  CatalogAuthConfigFieldDto,
  CatalogConnectionProfileDto,
  CatalogEntryDto,
  CatalogUrlPlaceholderDto,
  IntegrationAuthMode,
  IntegrationListItem,
} from '@greenlight/shared';
import {
  catalogProfileActsAsSignedInUser,
  connectionProfileForMode,
  defaultConnectionProfile,
  extractPlaceholderTokens,
  getCatalogEntry,
  hasUnresolvedPlaceholder,
  INTEGRATION_AUTH_MODES,
  isSecretFieldSource,
  matchUrlPlaceholders,
  placeholdersForTemplates,
} from '@greenlight/shared';

export { hasUnresolvedPlaceholder };

const HTTP_HEADER_NAME_RE = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;

export type CustomAuthInjectKind = 'bearer' | 'basic' | 'custom_header' | 'query_param';
export type BasicKeyPosition = 'username' | 'password';

export interface CustomServiceAccountFields {
  authInjectKind: CustomAuthInjectKind;
  basicKeyPosition: BasicKeyPosition;
  authHeaderName: string;
  authQueryParamName: string;
  authUsername: string;
}

// A `{placeholder}` token in a catalog base URL, token URL, or authorize URL — a
// per-customer host or tenant the registration form prompts IT to replace (ADR-0026
// note). Detection lives in `@greenlight/shared` so seed invariants and the form
// share one grammar.

export function urlPlaceholdersFor(
  entry: CatalogEntryDto,
  profile: CatalogConnectionProfileDto | null,
): CatalogUrlPlaceholderDto[] {
  return placeholdersForTemplates(
    [entry.base_url, profile?.token_url ?? '', profile?.authorize_url ?? ''],
    entry.placeholders,
  ).map((placeholder) => ({
    token: placeholder.token,
    label: placeholder.label,
    hint: placeholder.hint ?? null,
    example: placeholder.example ?? null,
    normalize: placeholder.normalize ?? null,
    format: placeholder.format ?? null,
  }));
}

/** The catalog templates an edit re-resolves, and the values the stored URLs came from. */
export interface EditUrlTemplates {
  placeholders: CatalogUrlPlaceholderDto[];
  values: Record<string, string>;
  /** Empty when the entry's base URL is literal, so the base URL stays a plain field. */
  baseUrl: string;
  tokenUrl: string;
  authorizeUrl: string;
}

/**
 * How the edit form corrects a registered integration's addresses (docs/28
 * §/integrations). A catalog integration whose stored URLs still fit its
 * profile's templates edits the placeholder values, so the base URL and the
 * token endpoint cannot drift apart. Any other integration that mints at a
 * token endpoint edits that URL as a plain field. GitHub App installations
 * keep their token URL fixed: the installation id is also part of the stored
 * secret.
 */
export type EditUrlShape =
  | { kind: 'placeholders'; templates: EditUrlTemplates }
  | { kind: 'token_url' }
  | { kind: 'base_url' };

export function editUrlShapeFor(
  integration: Pick<
    IntegrationListItem,
    'catalog_key' | 'auth_mode' | 'auth_config' | 'base_url' | 'token_endpoint_auth_method'
  >,
): EditUrlShape {
  const mode = integration.auth_mode;
  const mintsAtTokenUrl = mode === 'client_credentials' || mode === 'user_delegated';
  if (!mintsAtTokenUrl || integration.token_endpoint_auth_method === 'github_app') {
    return { kind: 'base_url' };
  }
  const fallback: EditUrlShape = { kind: 'token_url' };
  const entry = getCatalogEntry(integration.catalog_key);
  if (!entry || !(INTEGRATION_AUTH_MODES as readonly string[]).includes(mode)) return fallback;
  const config = integration.auth_config ?? {};
  const mechanism = config['mechanism'];
  const profile = connectionProfileForMode(
    entry,
    mode as IntegrationAuthMode,
    typeof mechanism === 'string' ? mechanism : null,
  );
  if (profile.tokenEndpointAuthMethod === 'github_app') return { kind: 'base_url' };

  const stored = (key: string): string => {
    const value = config[key];
    return typeof value === 'string' ? value : '';
  };
  const baseTemplate = extractPlaceholderTokens([entry.baseUrl]).length > 0 ? entry.baseUrl : '';
  const tokenTemplate = profile.tokenUrl ?? '';
  const authorizeTemplate = mode === 'user_delegated' ? (profile.authorizeUrl ?? '') : '';
  const pairs = [
    { template: entry.baseUrl, url: integration.base_url ?? '' },
    ...(tokenTemplate ? [{ template: tokenTemplate, url: stored('token_url') }] : []),
    ...(authorizeTemplate ? [{ template: authorizeTemplate, url: stored('authorize_url') }] : []),
  ];
  const values = matchUrlPlaceholders(pairs);
  if (values === null) return fallback;
  if (Object.keys(values).length === 0) return { kind: 'base_url' };
  return {
    kind: 'placeholders',
    templates: {
      placeholders: placeholdersForTemplates(
        [baseTemplate, tokenTemplate, authorizeTemplate],
        entry.placeholders,
      ).map((placeholder) => ({
        token: placeholder.token,
        label: placeholder.label,
        hint: placeholder.hint ?? null,
        example: placeholder.example ?? null,
        normalize: placeholder.normalize ?? null,
        format: placeholder.format ?? null,
      })),
      values,
      baseUrl: baseTemplate,
      tokenUrl: tokenTemplate,
      authorizeUrl: authorizeTemplate,
    },
  };
}

/** The MVP-selectable connection profiles of an entry (Tier 3 ones are non-selectable). */
export function selectableProfiles(entry: CatalogEntryDto): CatalogConnectionProfileDto[] {
  return entry.connection_profiles.filter((p) => p.mvp_selectable);
}

/** The pre-selected profile: the default when selectable, else the first selectable one. */
export function defaultSelectableProfile(
  entry: CatalogEntryDto,
): CatalogConnectionProfileDto | undefined {
  const selectable = selectableProfiles(entry);
  return selectable.find((p) => p.is_default) ?? selectable[0];
}

/**
 * Build the params-only `auth_config` to submit for a connection profile (docs/09 §
 * Tiered Auth Model). `service_account` carries its wire shape (`auth_inject`);
 * `client_credentials` carries `token_url` + optional `scopes`; a per-user OAuth
 * `user_delegated` profile carries its mechanism, both endpoints, and the scopes
 * each user authorizes. Returns `undefined` when there is nothing to persist (e.g.
 * a public source with no credential).
 */
export function buildAuthConfig(
  profile: CatalogConnectionProfileDto,
  fields: {
    tokenUrl: string;
    authorizeUrl?: string;
    scopes: string[];
    username?: string;
    nonSecretParams?: Record<string, string>;
  },
): AuthConfig | undefined {
  if (profile.auth_mode === 'user_delegated') {
    return {
      mechanism: 'authorization_code',
      authorize_url: fields.authorizeUrl ?? '',
      token_url: fields.tokenUrl,
      scopes: fields.scopes,
    };
  }
  if (profile.auth_mode === 'service_account') {
    if (!profile.auth_inject) {
      // No static wire shape — the proxy mints upstream tokens itself
      // (google-workspace DWD, docs/09) and needs the OAuth scopes to mint with.
      return fields.scopes.length > 0 ? { scopes: fields.scopes } : undefined;
    }
    const config: AuthConfig = { auth_inject: profile.auth_inject };
    if (
      profile.auth_inject.kind === 'basic' &&
      profile.auth_inject.username_from !== 'secret' &&
      fields.username
    ) {
      config[profile.auth_inject.username_from] = fields.username;
    }
    if (profile.auth_inject.kind === 'multi_header') {
      for (const param of nonSecretAuthParamNames(profile)) {
        const value = fields.nonSecretParams?.[param]?.trim();
        if (value) config[param] = value;
      }
    }
    return config;
  }
  if (profile.auth_mode === 'client_credentials') {
    const config: AuthConfig = { token_url: fields.tokenUrl };
    if (fields.scopes.length > 0) config.scopes = fields.scopes;
    for (const field of profile.auth_config_fields) {
      const value = fields.nonSecretParams?.[field.key]?.trim();
      if (value) config[field.key] = value;
    }
    if (profile.token_endpoint_auth_method !== null) {
      config.token_endpoint_auth_method = profile.token_endpoint_auth_method;
    }
    return config;
  }
  return undefined;
}

/** Errors for a profile's own non-secret fields (Box's Enterprise ID): required, and in format. */
export function authConfigFieldErrors(
  profile: CatalogConnectionProfileDto | null,
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  return authConfigFieldErrorsFor(profile?.auth_config_fields ?? [], values);
}

export function authConfigFieldErrorsFor(
  fields: readonly CatalogAuthConfigFieldDto[],
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const value = values[field.key]?.trim() ?? '';
    if (value === '') errors[field.key] = 'Required';
    else if (field.format === 'digits' && !/^\d+$/.test(value)) errors[field.key] = 'Digits only';
  }
  return errors;
}

/** Non-secret auth_config fields a catalogued static-key profile must collect. */
export function nonSecretAuthParamNames(profile: CatalogConnectionProfileDto | null): string[] {
  const inject = profile?.auth_inject;
  if (inject?.kind !== 'multi_header') return [];
  return [
    ...new Set(
      inject.headers
        .map((header) => header.value_from)
        .filter((v) => v !== 'secret' && !isSecretFieldSource(v)),
    ),
  ];
}

export function buildCustomServiceAccountConfig(fields: CustomServiceAccountFields): AuthConfig {
  let authInject: AuthInject;
  switch (fields.authInjectKind) {
    case 'bearer':
      authInject = { kind: 'bearer' };
      break;
    case 'basic':
      authInject = {
        kind: 'basic',
        username_from: fields.basicKeyPosition === 'password' ? 'username' : 'secret',
      };
      break;
    case 'custom_header':
      authInject = { kind: 'custom_header', header_name: fields.authHeaderName.trim() };
      break;
    case 'query_param':
      authInject = { kind: 'query_param', param_name: fields.authQueryParamName.trim() };
      break;
  }
  return {
    auth_inject: authInject,
    ...(authInject.kind === 'basic' && authInject.username_from !== 'secret'
      ? { username: fields.authUsername.trim() }
      : {}),
  };
}

/**
 * Stopgap for Client Credentials registration UX: IT enters client id as a form
 * field and the secret (or private key) as the credential. The proxy still reads
 * the vaulted secret as JSON (docs/09 §Tier 2) — compose that blob at submit time.
 */
export function composeClientCredentialsSecret(clientId: string, clientSecret: string): string {
  return JSON.stringify({
    client_id: clientId.trim(),
    client_secret: clientSecret,
  });
}

export function composePrivateKeyJwtSecret(
  clientId: string,
  certificateId: string,
  privateKey: string,
): string {
  return JSON.stringify({
    client_id: clientId.trim(),
    certificate_id: certificateId.trim(),
    private_key: privateKey,
  });
}

export function isPrivateKeyJwt(profile: CatalogConnectionProfileDto | null): boolean {
  return profile?.token_endpoint_auth_method === 'private_key_jwt';
}

/**
 * The role each credential of a registered integration is (Snowflake), read from
 * its catalog profile; null when the profile does not make credentials roles.
 */
export function credentialRoleFor(
  catalogKey: string | null | undefined,
  authMode?: string | null,
  mechanism?: unknown,
): CatalogCredentialRole | null {
  const entry = getCatalogEntry(catalogKey);
  if (!entry || authMode !== 'user_delegated') return null;
  return (
    connectionProfileForMode(
      entry,
      'user_delegated',
      typeof mechanism === 'string' ? mechanism : null,
    ).credentialRole ?? null
  );
}

/**
 * The customer-specific auth_config fields a registered integration's catalog
 * profile collects (Box's Enterprise ID). Edit has no picked profile, so it
 * reads them from the seed, as `collectMintScopesFor` does.
 */
export function authConfigFieldsFor(
  catalogKey: string | null | undefined,
  authMode?: string | null,
): CatalogAuthConfigFieldDto[] {
  const entry = getCatalogEntry(catalogKey);
  if (!entry) return [];
  const profile =
    authMode && (INTEGRATION_AUTH_MODES as readonly string[]).includes(authMode)
      ? connectionProfileForMode(entry, authMode as IntegrationAuthMode)
      : defaultConnectionProfile(entry);
  return (profile?.authConfigFields ?? []).map((field) => ({
    key: field.key,
    label: field.label,
    hint: field.hint ?? null,
    example: field.example ?? null,
    format: field.format ?? null,
  }));
}

/** Whether a registered integration's catalog profile acts as the signed-in user (Box). */
export function actsAsSignedInUserFor(
  catalogKey: string | null | undefined,
  authMode: string,
): boolean {
  return (
    (INTEGRATION_AUTH_MODES as readonly string[]).includes(authMode) &&
    catalogProfileActsAsSignedInUser(catalogKey, authMode as IntegrationAuthMode)
  );
}

/** Whether the catalog profile collects mint scopes — used at register and edit. */
export function collectMintScopesFor(
  catalogKey: string | null | undefined,
  authMode?: string | null,
  mechanism?: unknown,
): boolean {
  const entry = getCatalogEntry(catalogKey);
  if (!entry) return false;
  const profile =
    authMode && (INTEGRATION_AUTH_MODES as readonly string[]).includes(authMode)
      ? connectionProfileForMode(
          entry,
          authMode as IntegrationAuthMode,
          typeof mechanism === 'string' ? mechanism : null,
        )
      : defaultConnectionProfile(entry);
  return profile?.collectMintScopes === true;
}

/**
 * The org-owned GitHub App profile: the proxy mints installation tokens from a
 * vaulted App key, so registration collects the App ID, the installation ID,
 * and the PEM rather than an OAuth client id/secret (docs/09 §Tier 2).
 */
export function isGithubApp(profile: CatalogConnectionProfileDto | null): boolean {
  return profile?.token_endpoint_auth_method === 'github_app';
}

export function composeGithubAppSecret(
  appId: string,
  installationId: string,
  privateKey: string,
): string {
  return JSON.stringify({
    app_id: appId.trim(),
    installation_id: installationId.trim(),
    private_key: privateKey,
  });
}

export function isValidHeaderName(value: string): boolean {
  return value.length <= 255 && HTTP_HEADER_NAME_RE.test(value);
}

export function isValidQueryParamName(value: string): boolean {
  if (value.length === 0 || value.length > 255) return false;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 31 || codePoint === 127 || '=&?#'.includes(character)) return false;
  }
  return true;
}

export function credentialPlacementLabel(
  inject: CatalogConnectionProfileDto['auth_inject'],
  isClientCreds: boolean,
): string {
  if (isClientCreds) return 'Authorization header · Bearer OAuth token';
  if (!inject) return 'Provider-specific or no credential placement';
  switch (inject.kind) {
    case 'bearer':
      return 'Authorization header · Bearer token';
    case 'basic':
      return inject.username_from === 'secret'
        ? 'Authorization header · Basic (API key as username)'
        : 'Authorization header · Basic (API key as password)';
    case 'custom_header':
      return `Header · ${inject.header_name}`;
    case 'query_param':
      return `Query parameter · ${inject.param_name}`;
    case 'multi_header':
      return `Headers · ${inject.headers.map((header) => header.header_name).join(', ')}`;
  }
}

/** Parse the scopes textarea (whitespace/comma separated) into a clean string array. */
export function parseScopes(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export interface CatalogPrefill {
  displayName: string;
  name: string;
  baseUrl: string;
  authMode: IntegrationAuthMode;
  tokenUrl: string;
  authorizeUrl: string;
  scopes: string;
  /** Connected-database default port (ADR-0030); '' for HTTP entries. */
  port: string;
}

/**
 * The machine name the form suggests. A provider registered once per identity
 * model keeps its key for the default profile and suffixes the others: `<key>-user`
 * for a per-user OAuth registration, `<key>-app` for an app-only one (docs/09
 * §Integrations that support both identity models), so they never collide.
 */
export function suggestedCatalogSlug(
  entry: CatalogEntryDto,
  profile: CatalogConnectionProfileDto,
): string {
  if (profile.is_default) return entry.catalog_key;
  if (profile.auth_mode === 'user_delegated') return `${entry.catalog_key}-user`;
  if (profile.auth_mode === 'client_credentials') return `${entry.catalog_key}-app`;
  return entry.catalog_key;
}

/** A display name as a machine name: lowercase ASCII words joined by hyphens, at most 63 characters. */
export function slugFromDisplayName(displayName: string): string {
  return displayName
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

const CREDENTIAL_SLUG_SUFFIX = '-credential';

/**
 * The credential identifier registration proposes: the machine name plus
 * `-credential`, with the machine name cut so the result stays within the
 * 63-character slug limit. Falls back to `credential` when the machine name is
 * not a valid identifier.
 */
export function defaultCredentialSlugFor(machineName: string): string {
  const name = machineName.trim();
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(name)) return 'credential';
  const base = name.slice(0, 63 - CREDENTIAL_SLUG_SUFFIX.length).replace(/-+$/, '');
  return `${base}${CREDENTIAL_SLUG_SUFFIX}`;
}

/**
 * The machine name that follows the display name until IT edits it: the catalog
 * suggestion while the display name is still the catalog's own, otherwise the
 * display name slugged.
 */
export function machineNameFor(
  displayName: string,
  entry: CatalogEntryDto | null,
  profile: CatalogConnectionProfileDto | null,
): string {
  if (entry && profile && displayName.trim() === entry.display_name) {
    return suggestedCatalogSlug(entry, profile);
  }
  return slugFromDisplayName(displayName);
}

/** Initial form values when registering from a catalog entry + chosen profile. */
export function prefillFromCatalog(
  entry: CatalogEntryDto,
  profile: CatalogConnectionProfileDto,
): CatalogPrefill {
  return {
    displayName: entry.display_name,
    name: suggestedCatalogSlug(entry, profile),
    baseUrl: entry.base_url,
    authMode: profile.auth_mode,
    tokenUrl: profile.token_url ?? '',
    authorizeUrl: profile.authorize_url ?? '',
    scopes: (profile.scopes ?? []).join(' '),
    port: entry.default_port !== null ? String(entry.default_port) : '',
  };
}

export interface ApiError {
  code: string;
  message: string;
}

/**
 * Narrow a caught value to the Greenlight error shape `{ code, message }` (docs/16).
 * The ts-rest client throws a non-2xx response as `{ status, body }`, so the
 * error body is one level in; a network/runtime throw has none.
 */
export function extractApiError(err: unknown): ApiError | null {
  const body =
    err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
  if (
    body &&
    typeof body === 'object' &&
    typeof (body as { code?: unknown }).code === 'string' &&
    typeof (body as { message?: unknown }).message === 'string'
  ) {
    return { code: (body as ApiError).code, message: (body as ApiError).message };
  }
  return null;
}
